import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export function useSponsorInfo(
  sponsorId: string | undefined,
  isSponsored: boolean,
) {
  return useQuery({
    queryKey: ["sponsor", sponsorId],
    queryFn: async () => {
      if (!isSponsored || !sponsorId) return null;

      console.log("Fetching sponsor info for:", sponsorId);

      // Use targeted RPC for single profile lookup
      const { data: profiles } = await supabase.rpc("get_profiles_by_ids", {
        p_ids: [sponsorId],
      });

      const publicProfile = profiles?.[0] || null;

      if (publicProfile) {
        console.log("Sponsor public profile retrieved:", publicProfile);
        return {
          ...publicProfile,
          // Ensure we have the sponsor username for routing
          sponsorUsername: publicProfile.username,
        };
      }

      // If not found in public_profiles, try getting from sponsor_profiles via safe RPC
      const { data: allSponsors, error: sponsorError } = await supabase.rpc(
        "get_sponsor_profiles_for_discovery",
      );
      const sponsorProfile =
        allSponsors?.find((s: any) => s.id === sponsorId) ?? null;

      if (sponsorError) {
        console.error("Error fetching sponsor:", sponsorError);
        return null;
      }

      console.log("Sponsor profile retrieved:", sponsorProfile);

      // Map sponsor_profiles data to match public_profiles format
      return sponsorProfile
        ? {
            display_name: sponsorProfile.name,
            avatar_url: sponsorProfile.avatar_url,
            id: sponsorProfile.id,
            username: sponsorProfile.company_name || "sponsor",
            bio: sponsorProfile.bio,
            is_verified: false, // Default for sponsor_profiles as they don't have this field
            website: sponsorProfile.website,
            // Use company_name as fallback for username
            sponsorUsername: sponsorProfile.company_name || "sponsor",
          }
        : null;
    },
    enabled: isSponsored && !!sponsorId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}
